import React, { useState, useEffect } from 'react';
import {
  Stack,
  Title,
  Button,
  Group,
  Table,
  Badge,
  ActionIcon,
  Paper,
  Text,
  Modal,
  TextInput,
  Select,
  Pagination,
  Loader,
  Center,
  Grid,
  Card
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconEye,
  IconTrash,
  IconSearch,
  IconRefresh,
  IconVideo,
  IconLivePhoto,
  IconQuestionMark,
  IconDownload
} from '@tabler/icons-react';
import { classService } from '../../services/classService';
import ClassForm from '../../components/admin/ClassForm';
import ClassDetails from '../../components/admin/ClassDetails';

const ClassesManager = () => {
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [formMode, setFormMode] = useState('create');
  
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [selectedType, setSelectedType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [formOpened, formHandlers] = useDisclosure(false);
  const [detailsOpened, detailsHandlers] = useDisclosure(false);
  const [deleteOpened, deleteHandlers] = useDisclosure(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadClasses();
  }, [debouncedSearch, selectedType, currentPage]);

  const loadInitialData = async () => {
    try {
      const statsData = await classService.getStats();
      setStats(statsData);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load statistics',
        color: 'red',
      });
    }
  };

  const loadClasses = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 20,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(selectedType && { type: selectedType }),
      };
      
      const data = await classService.getClasses(params);
      setClasses(data.data);
      setTotalPages(data.last_page);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load classes',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedClass(null);
    setFormMode('create');
    formHandlers.open();
  };

  const handleEdit = (classItem) => {
    setSelectedClass(classItem);
    setFormMode('edit');
    formHandlers.open();
  };

  const handleView = (classItem) => {
    setSelectedClass(classItem);
    detailsHandlers.open();
  };

  const handleDelete = (classItem) => {
    setSelectedClass(classItem);
    deleteHandlers.open();
  };

  const confirmDelete = async () => {
    try {
      await classService.deleteClass(selectedClass.id);
      notifications.show({
        title: 'Success',
        message: 'Class deleted successfully',
        color: 'green',
      });
      deleteHandlers.close();
      setSelectedClass(null);
      loadClasses();
      loadInitialData();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete class';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    }
  };

  const handleFormSubmit = () => {
    formHandlers.close();
    setSelectedClass(null);
    loadClasses();
    loadInitialData();
  };

  const clearFilters = () => {
    setSearchValue('');
    setSelectedType('');
    setCurrentPage(1);
  };

  const getTypeBadge = (type) => {
    return type === 'video_class' 
      ? <Badge color="blue" leftSection={<IconVideo size={12} />}>Video Class</Badge>
      : <Badge color="green" leftSection={<IconLivePhoto size={12} />}>Live Class</Badge>;
  };

  const getFeatureBadges = (classItem) => {
    const badges = [];
    if (classItem.quiz_json && classItem.quiz_json.length > 0) {
      badges.push(
        <Badge key="quiz" size="xs" color="purple" leftSection={<IconQuestionMark size={10} />}>
          Quiz
        </Badge>
      );
    }
    if (classItem.downloadable_material_links_json && classItem.downloadable_material_links_json.length > 0) {
      badges.push(
        <Badge key="download" size="xs" color="orange" leftSection={<IconDownload size={10} />}>
          Materials
        </Badge>
      );
    }
    return badges;
  };

  const StatCard = ({ icon: Icon, title, value, color = 'blue' }) => (
    <Card withBorder p="md">
      <Group>
        <Icon size={24} color={`var(--mantine-color-${color}-6)`} />
        <Stack gap="xs">
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text size="lg" fw={700}>
            {value}
          </Text>
        </Stack>
      </Group>
    </Card>
  );

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={1}>Classes Manager</Title>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => {
              loadClasses();
              loadInitialData();
            }}
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreate}
          >
            Create Class
          </Button>
        </Group>
      </Group>

      {/* Stats Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconVideo}
            title="Total Classes"
            value={stats.total_classes || 0}
            color="blue"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconVideo}
            title="Video Classes"
            value={stats.video_classes || 0}
            color="purple"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconLivePhoto}
            title="Live Classes"
            value={stats.live_classes || 0}
            color="green"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconQuestionMark}
            title="With Quiz"
            value={stats.classes_with_quiz || 0}
            color="orange"
          />
        </Grid.Col>
      </Grid>

      {/* Filters */}
      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Search by title or description..."
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by type"
            data={[
              { value: '', label: 'All Types' },
              { value: 'video_class', label: 'Video Classes' },
              { value: 'live_class', label: 'Live Classes' }
            ]}
            value={selectedType}
            onChange={setSelectedType}
            clearable
          />
          <Button variant="light" onClick={clearFilters}>
            Clear
          </Button>
        </Group>
      </Paper>

      {/* Classes Table */}
      {loading ? (
        <Center h={400}>
          <Loader size="lg" />
        </Center>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Features</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {classes.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Center p="xl">
                      <Stack align="center">
                        <IconVideo size={48} color="gray" />
                        <Text c="dimmed">No classes found</Text>
                        <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
                          Create First Class
                        </Button>
                      </Stack>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : (
                classes.map((classItem) => (
                  <Table.Tr key={classItem.id}>
                    <Table.Td>
                      <Text fw={500}>{classItem.title}</Text>
                    </Table.Td>
                    <Table.Td>{getTypeBadge(classItem.type)}</Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={2}>
                        {classItem.short_description}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {getFeatureBadges(classItem)}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {new Date(classItem.created_at).toLocaleDateString('en-IN')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon
                          variant="light"
                          size="sm"
                          onClick={() => handleView(classItem)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          size="sm"
                          onClick={() => handleEdit(classItem)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="red"
                          size="sm"
                          onClick={() => handleDelete(classItem)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>

          {totalPages > 1 && (
            <Group justify="center" p="md">
              <Pagination
                value={currentPage}
                onChange={setCurrentPage}
                total={totalPages}
              />
            </Group>
          )}
        </Paper>
      )}

      {/* Modals */}
      <Modal
        opened={formOpened}
        onClose={() => {
          formHandlers.close();
          setSelectedClass(null);
        }}
        title={formMode === 'create' ? 'Create New Class' : 'Edit Class'}
        size="xl"
      >
        <ClassForm
          classItem={selectedClass}
          mode={formMode}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            formHandlers.close();
            setSelectedClass(null);
          }}
        />
      </Modal>

      <Modal
        opened={detailsOpened}
        onClose={detailsHandlers.close}
        title="Class Details"
        size="xl"
      >
        {selectedClass && (
          <ClassDetails classItem={selectedClass} />
        )}
      </Modal>

      <Modal
        opened={deleteOpened}
        onClose={deleteHandlers.close}
        title="Confirm Delete"
      >
        <Stack>
          <Text>
            Are you sure you want to delete class "{selectedClass?.title}"? 
            This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="light" onClick={deleteHandlers.close}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDelete}>
              Delete Class
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default ClassesManager;